import { surveyAnswerType, type ServiceDetail, type SurveyAnswerType } from "@shyne/shared/api";
import { ReadAllResponse, uuid } from "@/interfaces";
import { activeOrganizationIdIfAny } from "@/lib/active-organization";
import { api } from "@/lib/api";
import { Survey, SurveyQuestionDraft } from "@/models/survey.model";

const SCREEN_ANSWER_TYPE: Record<SurveyAnswerType, string> = { yesNo: "boolean", choice: "select", text: "text" };

export const answerTypeChosen = (screenAnswerType: string): SurveyAnswerType =>
  surveyAnswerType.options.find((answerType) => SCREEN_ANSWER_TYPE[answerType] === screenAnswerType) ?? "text";

export class SurveyQuestionsUnread extends Error {
  constructor() {
    super("Only the vendor's admins read a parent service's survey questions.");
  }
}

export function toSurveyList({ id: service, surveyQuestions }: Pick<ServiceDetail, "id" | "surveyQuestions">): ReadAllResponse<Survey> {
  if (surveyQuestions === undefined) throw new SurveyQuestionsUnread();
  return {
    data: surveyQuestions.map((question) => ({
      id: question.id,
      question: question.question,
      answer_type: SCREEN_ANSWER_TYPE[question.answerType],
      service,
      options: question.options,
      created_at: "",
      updated_at: "",
    })),
    count: surveyQuestions.length,
  };
}

export const optionsTyped = (options: string | undefined): string[] =>
  (options ?? "")
    .split(",")
    .map((option) => option.trim())
    .filter((option) => option !== "");

export const SurveyRepository = {
  async getAll(service: uuid): Promise<ReadAllResponse<Survey>> {
    return toSurveyList(await api.services.get(service, { actingOrganization: activeOrganizationIdIfAny() }));
  },
  async createSurveyQuestion(service: uuid, { question, answer_type, options }: SurveyQuestionDraft): Promise<ReadAllResponse<Survey>> {
    const answerType = answerTypeChosen(answer_type);
    const typed = optionsTyped(options);
    return toSurveyList(await api.services.addSurveyQuestion(service, { question, answerType, ...(answerType === "choice" && typed.length > 0 ? { options: typed } : {}) }));
  },
  async deleteSurveyQuestion(service: uuid, question: uuid): Promise<ReadAllResponse<Survey>> {
    return toSurveyList(await api.services.deleteSurveyQuestion(service, question));
  },
};
